import { db } from '@/shared/db/database';
import type { LocalFaceEmbedding } from '@/shared/db/database';
import { shouldOverwriteEmbedding } from '../../domain/valueObjects/FaceEmbeddingSource';

export interface RemoteEmbeddingCandidate {
  idPerson: number;
  vector: number[];
}

export interface BulkSeedResult {
  seeded: number;
  skipped: number;
}

const BULK_SEED_CHUNK_SIZE = 500;

export class LocalFaceEmbeddingRepository {
  async upsert(record: LocalFaceEmbedding): Promise<void> {
    await db.faceEmbeddings.put(record);
  }

  async get(idPerson: number, idAccessRole: number): Promise<LocalFaceEmbedding | null> {
    const row = await db.faceEmbeddings.get([idPerson, idAccessRole] as unknown as string);
    return row ?? null;
  }

  async getByAccessRole(idAccessRole: number): Promise<LocalFaceEmbedding[]> {
    return db.faceEmbeddings.where('idAccessRole').equals(idAccessRole).toArray();
  }

  async markSynced(idPerson: number, idAccessRole: number): Promise<void> {
    await db.faceEmbeddings.update([idPerson, idAccessRole] as unknown as string, { syncStatus: 'synced' });
  }

  async listPending(): Promise<LocalFaceEmbedding[]> {
    return db.faceEmbeddings.where('syncStatus').equals('pending').toArray();
  }

  /**
   * Removes ALL face-embedding rows for the given employee, across every
   * idAccessRole they belong to.
   *
   * Used by `DeleteFacialEmbeddingUseCase` so a remote deletion is mirrored
   * locally: the offline matcher MUST stop recognizing the deleted employee
   * immediately, otherwise the kiosk would still grant entry by face after
   * an admin removed the enrollment.
   *
   * Returns the number of rows deleted (0 when the employee had no embeddings).
   */
  async deleteByIdPerson(idPerson: number): Promise<number> {
    return db.faceEmbeddings.where('idPerson').equals(idPerson).delete();
  }

  /**
   * Bulk-seed face embeddings from remote person_embed_v2 vectors.
   * Runs inside a single Dexie rw transaction, chunked at BULK_SEED_CHUNK_SIZE.
   * Never overwrites rows with source='local-enrolled'.
   * Null-safe: entries with null/undefined or wrong-length vectors are skipped.
   */
  async bulkSeedFromRemote(
    idAccessRole: number,
    candidates: readonly RemoteEmbeddingCandidate[],
    options?: { chunkSize?: number },
  ): Promise<BulkSeedResult> {
    const chunkSize = options?.chunkSize ?? BULK_SEED_CHUNK_SIZE;

    // Filter out invalid vectors before touching the DB
    const valid = candidates.filter(
      (c) => Array.isArray(c.vector) && c.vector.length === 512,
    );
    const invalidCount = candidates.length - valid.length;

    if (valid.length === 0) {
      return { seeded: 0, skipped: invalidCount };
    }

    let totalSeeded = 0;
    let totalSkipped = invalidCount;

    // Process in chunks to avoid large transactions on big rosters
    for (let offset = 0; offset < valid.length; offset += chunkSize) {
      const chunk = valid.slice(offset, offset + chunkSize);
      const chunkPersonIds = chunk.map((c) => c.idPerson);

      const { chunkSeeded, chunkSkipped } = await db.transaction(
        'rw',
        db.faceEmbeddings,
        async () => {
          // Read existing rows for this chunk in one query
          const existing = await db.faceEmbeddings
            .where('[idPerson+idAccessRole]')
            .anyOf(chunkPersonIds.map((id) => [id, idAccessRole]))
            .toArray();

          const sourceMap = new Map<number, 'remote-v2' | 'local-enrolled'>(
            existing.map((r) => [r.idPerson, r.source]),
          );

          const toWrite: LocalFaceEmbedding[] = [];
          let skipped = 0;

          for (const candidate of chunk) {
            const existingSource = sourceMap.get(candidate.idPerson) ?? null;
            if (!shouldOverwriteEmbedding(existingSource, 'remote-v2')) {
              skipped++;
              continue;
            }
            toWrite.push({
              idPerson: candidate.idPerson,
              idAccessRole,
              embedding: candidate.vector,
              dim: 512,
              source: 'remote-v2',
              syncStatus: 'synced',
              updatedAt: Date.now(),
            });
          }

          if (toWrite.length > 0) {
            await db.faceEmbeddings.bulkPut(toWrite);
          }

          return { chunkSeeded: toWrite.length, chunkSkipped: skipped };
        },
      );

      totalSeeded += chunkSeeded;
      totalSkipped += chunkSkipped;
    }

    return { seeded: totalSeeded, skipped: totalSkipped };
  }
}
